<template>
  <div class="card-window" :style="[{ height }, cssVars]">
    <div class="chat-container">
      <rooms-list
        v-if="!singleRoom"
        :currentUserId="currentUserId"
        :rooms="orderedRooms"
        :loadingRooms="loadingRooms"
        :room="activeRoom ? activeRoom : {}"
        :textMessages="t"
        :showAddRoom="showAddRoom"
        :showRoomsList="showRoomsList"
        :textFormatting="textFormatting"
        :isMobile="isMobile"
        @openRoom="openRoom"
        @addRoom="addRoom"
        :searchBoxPlace="searchBoxPlace"
      >
        <template v-slot:rooms-header-logo>
          <slot name="rooms-header-logo"></slot>
        </template>
        <template v-slot:rooms-header>
          <slot name="rooms-header"></slot>
        </template>
        <template v-slot:rooms-footer>
          <slot name="rooms-footer"></slot>
        </template>
        <template v-slot:rooms-item="slotProps">
          <slot name="rooms-item" v-bind="slotProps"></slot>
        </template>

        <template v-slot:rooms-empty>
          <slot name="rooms-empty"></slot>
        </template>
      </rooms-list>

      <room
        :activeRoom="activeRoom"
        :currentUserId="currentUserId"
        :rooms="rooms"
        :roomId="room && room.roomId ? room.roomId : ''"
        :messages="messages"
        :roomMessage="roomMessage"
        :messagesLoaded="messagesLoaded"
        :menuActions="menuActions"
        :messageActions="messageActions"
        :showFiles="showFiles"
        :showEmojis="showEmojis"
        :showReactionEmojis="showReactionEmojis"
        :textMessages="t"
        :singleRoom="singleRoom"
        :showRoomsList="showRoomsList"
        :textFormatting="textFormatting"
        :isMobile="isMobile"
        :loadingRooms="loadingRooms"
        :roomInfo="$listeners.roomInfo"
        @toggleRoomsList="toggleRoomsList"
        @roomInfo="roomInfo"
        @fetchMessages="fetchMessages"
        @sendMessage="sendMessage"
        @editMessage="editMessage"
        @deleteMessage="deleteMessage"
        @openFile="openFile"
        @menuActionHandler="menuActionHandler"
        @messageActionHandler="messageActionHandler"
        @sendMessageReaction="sendMessageReaction"
        @typingMessage="typingMessage"
      >
        <template v-slot:room-buttons>
          <slot name="room-buttons"></slot>
        </template>
        <template v-slot:room-send>
          <slot name="room-send"></slot>
        </template>
        <template v-slot:room-empty>
          <slot name="room-empty"></slot>
        </template>
        <template v-slot:image="slotProps">
          <slot name="image" v-bind="slotProps"></slot>
        </template>
        <template v-slot:room-info="slotProps">
          <slot name="room-info" v-bind="slotProps"></slot>
        </template>
        <template v-slot:room-box-footer>
          <slot name="room-box-footer"></slot>
        </template>
      </room>
    </div>
  </div>
</template>

<script>
import RoomsList from './RoomsList'
import Room from './Room'

import locales from '../locales'
import { defaultThemeStyles, cssThemeVars } from '../themes'

const { roomsValid, partcipantsValid } = require('../utils/roomValidation')

export default {
  name: 'chat-container',
  components: {
    RoomsList,
    Room
  },
  props: {
    activeRoom: { type: Object, default: null },
    height: { type: String, default: '600px' },
    theme: { type: String, default: 'light' },
    styles: { type: Object, default: () => ({}) },
    responsiveBreakpoint: { type: Number, default: 900 },
    singleRoom: { type: Boolean, default: false },
    textMessages: { type: Object, default: null },
    currentUserId: { type: [String, Number], default: '' },
    rooms: {
      type: Object, default: () => ({
        messages: [],
        matches: [],
      })
    },
    loadingRooms: { type: Boolean, default: false },
    messages: { type: Array, default: () => [] },
    messagesLoaded: { type: Boolean, default: false },
    menuActions: { type: Array, default: () => [] },
    messageActions: {
      type: Array,
      default: () => [
        { name: 'replyMessage', title: 'Reply' },
        { name: 'editMessage', title: 'Edit Message', onlyMe: true },
        { name: 'deleteMessage', title: 'Delete Message', onlyMe: true }
      ]
    },
    showAddRoom: { type: Boolean, default: true },
    showFiles: { type: Boolean, default: true },
    showEmojis: { type: Boolean, default: true },
    showReactionEmojis: { type: Boolean, default: true },
    textFormatting: { type: Boolean, default: false },
    newMessage: { type: Object, default: null },
    roomMessage: { type: String, default: '' },
    searchBoxPlace: { type: String, default: 'middle' },
  },
  data() {
    return {
      room: {},
      showRoomsList: true,
      isMobile: false
    }
  },
  watch: {
    rooms(newVal, oldVal) {
      if (newVal[0] && newVal.length !== oldVal.length) {
        if (!this.isMobile) {
          this.openRoom({ room: this.orderedRooms[0] })
        } else {
          this.showRoomsList = true
        }
      }
    },
    activeRoom(room) {
      if (room && room.profile_id) {
        if (this.isMobile) {
          this.showRoomsList = false
        }
      }
    },
    newMessage(val) {
      this.$set(this.messages, val.index, val.message)
    },
    showRoomsList() {
      this.emitWindowStatus();
    },
    isMobile() {
      this.emitWindowStatus();
    },
  },
  mounted() {
    this.updateResponsive()
    window.addEventListener('resize', ev => {
      if (ev.isTrusted) this.updateResponsive()
    })
  },
  computed: {
    t() {
      return {
        ...locales,
        ...this.textMessages
      }
    },
    cssVars() {
      const defaultStyles = defaultThemeStyles[this.theme]
      const customStyles = {}

      Object.keys(defaultStyles).map(key => {
        customStyles[key] = {
          ...defaultStyles[key],
          ...(this.styles[key] || {})
        }
      })

      return cssThemeVars(customStyles)
    },
    orderedRooms() {
      return this.rooms;
    }
  },
  methods: {
    updateResponsive() {
      this.isMobile = window.innerWidth < this.responsiveBreakpoint
    },
    toggleRoomsList() {
      this.showRoomsList = !this.showRoomsList
      if (this.isMobile) {
        this.room = {}
        if (this.showRoomsList) {
          this.$emit('closeRoom', this.room)
        }
      }
    },
    openRoom(roomDto) {
      this.$emit('openRoom', roomDto)
    },
    roomInfo() {
      this.$emit('roomInfo', this.room)
    },
    addRoom() {
      this.$emit('addRoom')
    },
    fetchMessages(options) {
      this.$emit('fetchMessages', { room: this.activeRoom, options })
    },
    sendMessage(message) {
      this.$emit('sendMessage', { ...message, roomId: this.room.roomId, profileId: this.room.profile_id })
    },
    editMessage(message) {
      this.$emit('editMessage', { ...message, roomId: this.room.roomId })
    },
    deleteMessage(messageId) {
      this.$emit('deleteMessage', { messageId, roomId: this.room.roomId })
    },
    openFile(message) {
      this.$emit('openFile', message)
    },
    menuActionHandler(ev) {
      this.$emit('menuActionHandler', { action: ev, roomId: this.activeRoom.roomId, profileId: this.activeRoom.profile_id, })
    },
    messageActionHandler(ev) {
      this.$emit('messageActionHandler', {
        ...ev,
        roomId: this.room.roomId
      })
    },
    sendMessageReaction(messageReaction) {
      this.$emit('sendMessageReaction', {
        ...messageReaction,
        roomId: this.room.roomId
      })
    },
    typingMessage(message) {
      this.$emit('typingMessage', {
        message,
        roomId: this.room.roomId
      })
    },
    emitWindowStatus() {
      this.$emit('windowStatus', {
        showRoomsList: this.showRoomsList,
        isMobile: this.isMobile,
      })
    }
  }
}
</script>

<style lang="scss">
@import '../styles/index.scss';

* {
  font-family: inherit;
}

a {
  color: #0d579c;
}

.card-window {
  width: 100%;
  display: block;
  max-width: 100%;
  background: var(--chat-content-bg-color);
  color: var(--chat-color);
  overflow-wrap: break-word;
  position: relative;
  white-space: normal;
  border: var(--chat-container-border);
  border-radius: var(--chat-container-border-radius);
  box-shadow: var(--chat-container-box-shadow);
}

.chat-container {
  height: 100%;
  display: flex;

  textarea,
  input[type='text'],
  input[type='search'] {
    -webkit-appearance: none;
  }
}
</style>
